import { type MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { Button } from '#app/components/ui/button.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { logos } from './logos/logos.ts'

export const meta: MetaFunction = () => [{ title: 'Music Bingo' }]

// Tailwind Grid cell classes lookup
const columnClasses: Record<(typeof logos)[number]['column'], string> = {
	1: 'xl:col-start-1 bg-violet-500 hover:bg-violet-500/80',
	2: 'xl:col-start-2 bg-lime-500 hover:bg-lime-500/80',
	3: 'xl:col-start-3 bg-cyan-500 hover:bg-cyan-500/80',
	4: 'xl:col-start-4 bg-rose-500 hover:bg-rose-500/80',
	5: 'xl:col-start-5 bg-amber-500 hover:bg-amber-500/80',
}

const columnClassesDetail: Record<(typeof logos)[number]['column'], string> = {
	1: 'bg-violet-600 dark:bg-violet-200',
	2: 'bg-lime-600 dark:bg-lime-200',
	3: 'bg-cyan-600 dark:bg-cyan-200',
	4: 'bg-rose-600 dark:bg-rose-200',
	5: 'bg-amber-600 dark:bg-amber-200',
}

const rowClasses: Record<(typeof logos)[number]['row'], string> = {
	1: 'xl:row-start-1',
	2: 'xl:row-start-2',
	3: 'xl:row-start-3',
	4: 'xl:row-start-4',
	5: 'xl:row-start-5',
	6: 'xl:row-start-6',
}

export default function Index() {
	const user = useOptionalUser()

	return (
		<main className="font-poppins grid h-full place-items-center">
			<div className="grid place-items-center px-4 py-10 xl:grid-cols-2">
				<div className="flex max-w-md flex-col items-center text-center xl:order-2 xl:items-start xl:text-left">
					<div className="animate-slide-top [animation-fill-mode:backwards] xl:animate-slide-left xl:[animation-delay:0.5s] xl:[animation-fill-mode:backwards]">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="currentColor"
							className="h-16 w-16"
						>
							<path
								fill-rule="evenodd"
								d="M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .658.122Z"
								clip-rule="evenodd"
							/>
						</svg>
					</div>
					<h1
						data-heading
						className="mt-8 animate-slide-top text-4xl font-medium text-foreground [animation-fill-mode:backwards] [animation-delay:0.3s] md:text-5xl xl:mt-4 xl:animate-slide-left xl:text-6xl xl:[animation-fill-mode:backwards] xl:[animation-delay:0.8s]"
					>
						music <b>bingo</b>
					</h1>
					<div
						data-paragraph
						className="mt-6 animate-slide-top text-xl/7 text-muted-foreground [animation-fill-mode:backwards] [animation-delay:0.8s] xl:mt-8 xl:animate-slide-left xl:text-xl/6 xl:leading-10 xl:[animation-fill-mode:backwards] xl:[animation-delay:1s]"
					>
						<p>
							Play music bingo with your friends. A great way to discover new
							music and compete with your friends.
						</p>
						<div className="mt-6 flex justify-center gap-2 lg:justify-start">
							<Button asChild size={'lg'}>
								<Link to="join">Join</Link>
							</Button>
							<Button size={'lg'} variant={'secondary'} asChild>
								{user ? (
									<Link
										reloadDocument // This is a workaround for what I believe, a bug in Remix
										to={`users/${user?.username}/cards/new`}
									>
										Create
									</Link>
								) : (
									<Link to="login">Create</Link>
								)}
							</Button>
						</div>
					</div>
				</div>
				<ul className="mt-10 flex max-w-3xl flex-wrap justify-center gap-2 sm:gap-4 xl:mt-0 xl:grid xl:grid-flow-col xl:grid-cols-5 xl:grid-rows-6">
					<TooltipProvider>
						{logos.map((logo, i) => (
							<li
								key={logo.href}
								className={cn(
									columnClasses[logo.column],
									rowClasses[logo.row],
									'animate-roll-reveal [animation-fill-mode:backwards]',
									'rounded-sm',
									'transition hover:-rotate-6',
								)}
								style={{ animationDelay: `${i * 0.07}s` }}
							>
								<Tooltip>
									{/* <TooltipTrigger asChild>
										<a
											href={logo.href}
											className="grid size-20 place-items-center rounded-2xl bg-violet-600/10 p-4 transition hover:-rotate-6 hover:bg-violet-600 dark:bg-violet-200 dark:hover:bg-violet-100 sm:size-24"
										>
											<img src={logo.src} alt="" />
										</a>
									</TooltipTrigger>
									<TooltipContent>{logo.alt}</TooltipContent> */}
									<div className="grid h-14 w-24 place-items-center p-4 sm:h-20 sm:w-32">
										<div
											className={cn(
												'h-1.5 w-full rounded-sm sm:h-2',
												columnClassesDetail[logo.column],
											)}
										></div>
										<div
											className={cn(
												'h-2.5 w-full rounded-sm sm:h-3',
												columnClassesDetail[logo.column],
											)}
										></div>
									</div>
								</Tooltip>
							</li>
						))}
					</TooltipProvider>
				</ul>
			</div>
		</main>
	)
}
